import React, { useState, useEffect, useCallback, useReducer } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { ViewManager } from '../../../core/view-manager';
import { ApiManager } from '../../../core/api-manager';
import EntityBrowser from './components/EntityBrowser';
import QueryPanel from './components/QueryPanel';
import LayoutEditor from './components/LayoutEditor';
import StylePanel from './components/StylePanel';
import PageSettings from './components/PageSettings';
import Toolbar from './components/Toolbar';
import { DEFAULT_REPORT_STATE, generateId } from './utils/constants';
import './style.css';

// Report state reducer
function reportReducer(state, action) {
  switch (action.type) {
    case 'SET_REPORT':
      return { ...action.payload };

    case 'SET_NAME':
      return { ...state, name: action.payload };

    case 'SET_PAGE_SETTINGS':
      return { ...state, pageSettings: { ...state.pageSettings, ...action.payload } };

    case 'SET_MARGINS':
      return {
        ...state,
        pageSettings: {
          ...state.pageSettings,
          margins: { ...state.pageSettings.margins, ...action.payload }
        }
      };

    case 'SET_ENTITY':
      return {
        ...state,
        query: {
          ...state.query,
          entity: action.payload,
          selectedFields: [],
          filters: [],
          joins: [],
          groupBy: [],
          orderBy: []
        }
      };

    case 'ADD_FIELD':
      if (state.query.selectedFields.find(f => f.id === action.payload.id)) {
        return state;
      }
      return {
        ...state,
        query: {
          ...state.query,
          selectedFields: [...state.query.selectedFields, action.payload]
        }
      };

    case 'REMOVE_FIELD':
      return {
        ...state,
        query: {
          ...state.query,
          selectedFields: state.query.selectedFields.filter(f => f.id !== action.payload)
        }
      };

    case 'SET_FIELDS':
      return {
        ...state,
        query: {
          ...state.query,
          selectedFields: action.payload
        }
      };

    case 'ADD_FILTER':
      return {
        ...state,
        query: {
          ...state.query,
          filters: [...state.query.filters, action.payload]
        }
      };

    case 'UPDATE_FILTER':
      return {
        ...state,
        query: {
          ...state.query,
          filters: state.query.filters.map(f =>
            f.id === action.payload.id ? { ...f, ...action.payload.updates } : f
          )
        }
      };

    case 'REMOVE_FILTER':
      return {
        ...state,
        query: {
          ...state.query,
          filters: state.query.filters.filter(f => f.id !== action.payload)
        }
      };

    case 'ADD_JOIN':
      return {
        ...state,
        query: {
          ...state.query,
          joins: [...state.query.joins, action.payload]
        }
      };

    case 'REMOVE_JOIN':
      return {
        ...state,
        query: {
          ...state.query,
          joins: state.query.joins.filter(j => j.id !== action.payload)
        }
      };

    case 'SET_GROUP_BY':
      return {
        ...state,
        query: {
          ...state.query,
          groupBy: action.payload
        }
      };

    case 'SET_ORDER_BY':
      return {
        ...state,
        query: {
          ...state.query,
          orderBy: action.payload
        }
      };

    case 'ADD_ELEMENT':
      const { zone, element } = action.payload;
      return {
        ...state,
        layout: {
          ...state.layout,
          [zone]: {
            ...state.layout[zone],
            elements: [...state.layout[zone].elements, element]
          }
        }
      };

    case 'UPDATE_ELEMENT':
      const { zone: updateZone, elementId, updates } = action.payload;
      return {
        ...state,
        layout: {
          ...state.layout,
          [updateZone]: {
            ...state.layout[updateZone],
            elements: state.layout[updateZone].elements.map(el =>
              el.id === elementId ? { ...el, ...updates } : el
            )
          }
        }
      };

    case 'REMOVE_ELEMENT':
      const { zone: removeZone, elementId: removeId } = action.payload;
      return {
        ...state,
        layout: {
          ...state.layout,
          [removeZone]: {
            ...state.layout[removeZone],
            elements: state.layout[removeZone].elements.filter(el => el.id !== removeId)
          }
        }
      };

    case 'REORDER_ELEMENTS':
      const { zone: reorderZone, fromIndex, toIndex } = action.payload;
      const elements = [...state.layout[reorderZone].elements];
      const [movedElement] = elements.splice(fromIndex, 1);
      elements.splice(toIndex, 0, movedElement);
      return {
        ...state,
        layout: {
          ...state.layout,
          [reorderZone]: {
            ...state.layout[reorderZone],
            elements
          }
        }
      };

    case 'SET_ZONE_HEIGHT':
      return {
        ...state,
        layout: {
          ...state.layout,
          [action.payload.zone]: {
            ...state.layout[action.payload.zone],
            height: action.payload.height
          }
        }
      };

    case 'NEW_REPORT':
      return { ...DEFAULT_REPORT_STATE, name: 'New Report' };

    default:
      return state;
  }
}

export default function CrystalCKD() {
  // Report state
  const [report, dispatch] = useReducer(reportReducer, DEFAULT_REPORT_STATE);

  // UI state
  const [entities, setEntities] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedElement, setSelectedElement] = useState(null);
  const [rightPanelTab, setRightPanelTab] = useState('fields'); // 'fields' | 'style'
  const [leftPanelTab, setLeftPanelTab] = useState('query'); // 'query' | 'page'
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [reportId, setReportId] = useState(null);

  // Local queries state (stored in report file, not database)
  const [localQueries, setLocalQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [queryData, setQueryData] = useState(null);

  // File input ref for opening files
  const fileInputRef = React.useRef(null);

  // Element list drag state
  const [draggedElementIndex, setDraggedElementIndex] = useState(null);

  // Grid settings
  const [gridSettings, setGridSettings] = useState({
    visible: true,
    snapEnabled: true,
    size: 5 // mm
  });

  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState(null);

  // Load entities on mount
  useEffect(() => {
    loadEntities();
  }, []);

  const loadEntities = () => {
    try {
      const props = ViewManager.getProperties();
      setEntities(props);
      setLoading(false);
    } catch (error) {
      console.error('Error loading entities:', error);
      setLoading(false);
    }
  };

  // Handle entity selection
  const handleEntitySelect = (entityName) => {
    dispatch({ type: 'SET_ENTITY', payload: entityName });
    setSelectedElement(null);
  };

  // Handle field add to query
  const handleFieldAdd = (field) => {
    dispatch({ type: 'ADD_FIELD', payload: field });
  };

  // Handle field remove from query
  const handleFieldRemove = (fieldId) => {
    dispatch({ type: 'REMOVE_FIELD', payload: fieldId });
  };

  // Handle filter operations
  const handleAddFilter = () => {
    const newFilter = {
      id: generateId(),
      field: '',
      operator: 'value',
      value: '',
      conjunction: 'AND'
    };
    dispatch({ type: 'ADD_FILTER', payload: newFilter });
  };

  const handleUpdateFilter = (filterId, updates) => {
    dispatch({ type: 'UPDATE_FILTER', payload: { id: filterId, updates } });
  };

  const handleRemoveFilter = (filterId) => {
    dispatch({ type: 'REMOVE_FILTER', payload: filterId });
  };

  // Handle element operations
  const handleAddElement = (zone, element) => {
    const newElement = {
      ...element,
      id: generateId()
    };
    dispatch({ type: 'ADD_ELEMENT', payload: { zone, element: newElement } });
    setSelectedElement({ zone, id: newElement.id });
  };

  const handleUpdateElement = (zone, elementId, updates) => {
    dispatch({ type: 'UPDATE_ELEMENT', payload: { zone, elementId, updates } });
  };

  const handleRemoveElement = (zone, elementId) => {
    dispatch({ type: 'REMOVE_ELEMENT', payload: { zone, elementId } });
    if (selectedElement?.id === elementId) {
      setSelectedElement(null);
    }
  };

  const handleSelectElement = (zone, elementId) => {
    if (elementId) {
      setSelectedElement({ zone, id: elementId });
      setRightPanelTab('style');
    } else {
      setSelectedElement(null);
    }
  };

  // Get selected element data
  const getSelectedElementData = () => {
    if (!selectedElement) return null;
    const { zone, id } = selectedElement;
    return report.layout[zone]?.elements.find(el => el.id === id);
  };

  // Handle style changes
  const handleStyleChange = (updates) => {
    if (selectedElement) {
      // Check for delete flag
      if (updates._delete) {
        handleRemoveElement(selectedElement.zone, selectedElement.id);
        return;
      }
      handleUpdateElement(selectedElement.zone, selectedElement.id, updates);
    }
  };

  // Copy selected element
  const handleCopyElement = useCallback(() => {
    if (!selectedElement) return;
    const elementData = getSelectedElementData();
    if (elementData) {
      setClipboard({ ...elementData });
      toast.info('Elemento copiato');
    }
  }, [selectedElement, report]);

  // Paste element from clipboard
  const handlePasteElement = useCallback(() => {
    if (!clipboard) {
      toast.warning('Nessun elemento da incollare');
      return;
    }

    // Create new element with new ID and offset position
    const newElement = {
      ...clipboard,
      id: generateId(),
      x: (clipboard.x || 0) + 5,
      y: (clipboard.y || 0) + 5
    };

    // Deep copy columns for tables
    if (newElement.columns) {
      newElement.columns = newElement.columns.map(col => ({
        ...col,
        id: 'col_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
      }));
    }

    dispatch({ type: 'ADD_ELEMENT', payload: { zone: 'body', element: newElement } });
    setSelectedElement({ zone: 'body', id: newElement.id });
    toast.success('Elemento incollato');
  }, [clipboard]);

  // Keyboard shortcuts for copy/paste
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if typing in input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Ctrl+C or Cmd+C - Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedElement) {
          e.preventDefault();
          handleCopyElement();
        }
      }

      // Ctrl+V or Cmd+V - Paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (clipboard) {
          e.preventDefault();
          handlePasteElement();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, clipboard, handleCopyElement, handlePasteElement]);

  // Handle element reorder (drag and drop in elements list)
  const handleElementDragStart = (e, index) => {
    setDraggedElementIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleElementDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleElementDrop = (e, toIndex) => {
    e.preventDefault();
    if (draggedElementIndex !== null && draggedElementIndex !== toIndex) {
      dispatch({
        type: 'REORDER_ELEMENTS',
        payload: { zone: 'body', fromIndex: draggedElementIndex, toIndex }
      });
    }
    setDraggedElementIndex(null);
  };

  const handleElementDragEnd = () => {
    setDraggedElementIndex(null);
  };

  // Handle page settings
  const handlePageSettingsChange = (updates) => {
    dispatch({ type: 'SET_PAGE_SETTINGS', payload: updates });
  };

  const handleMarginsChange = (updates) => {
    dispatch({ type: 'SET_MARGINS', payload: updates });
  };

  // Find all unique queryIds used in the layout elements
  const getUsedQueryIds = () => {
    const queryIds = new Set();

    Object.values(report.layout).forEach(zone => {
      if (zone.elements) {
        zone.elements.forEach(el => {
          // Check element-level queryId
          if (el.queryId) {
            queryIds.add(el.queryId);
          }
          // Check field-level queryId (for dataField elements)
          if (el.field?.queryId) {
            queryIds.add(el.field.queryId);
          }
          // Also check columns in tables
          if (el.columns) {
            el.columns.forEach(col => {
              if (col.queryId) {
                queryIds.add(col.queryId);
              }
              if (col.field?.queryId) {
                queryIds.add(col.field.queryId);
              }
            });
          }
        });
      }
    });

    return Array.from(queryIds);
  };

  // Execute all queries used in the layout for print preview
  const handleExecuteQuery = async () => {
    const usedQueryIds = getUsedQueryIds();

    // If no queries, just enable preview mode without data
    if (usedQueryIds.length === 0) {
      setPreviewData({});
      setPreviewMode(true);
      toast.info('Anteprima attivata (nessun dato da query)');
      return;
    }

    try {
      setLoading(true);
      const queryResults = {};

      for (const queryId of usedQueryIds) {
        // Find query in local queries
        const localQuery = localQueries.find(q => q.id === queryId);
        if (!localQuery) {
          toast.error(`Query ID ${queryId} non trovata`);
          continue;
        }

        const sql = localQuery.sql_query || localQuery.sql;
        if (!sql) {
          toast.error(`Query "${localQuery.name}" senza SQL`);
          continue;
        }

        const execResponse = await ApiManager.sendAuthenticatedRequest('/utils/crystal_execute_query', {
          sql: sql,
          preview: false,
          limit: 10000
        });

        if (execResponse.success === 1 && execResponse.body?.data) {
          queryResults[queryId] = {
            name: localQuery.name,
            columns: execResponse.body.columns,
            data: execResponse.body.data
          };
        } else {
          toast.error(`Errore esecuzione query "${localQuery.name}": ${execResponse.message || 'Errore sconosciuto'}`);
        }
      }

      // Always enable preview mode, even if some queries failed
      setPreviewData(queryResults);
      setPreviewMode(true);

      if (Object.keys(queryResults).length > 0) {
        const totalRows = Object.values(queryResults).reduce((sum, q) => sum + q.data.length, 0);
        toast.success(`Anteprima: ${Object.keys(queryResults).length} query, ${totalRows} righe totali`);
      } else {
        toast.info('Anteprima attivata (nessun dato caricato)');
      }
    } catch (error) {
      console.error('Error executing queries:', error);
      toast.error('Errore esecuzione query');
    } finally {
      setLoading(false);
    }
  };

  // New report
  const handleNewReport = () => {
    dispatch({ type: 'NEW_REPORT' });
    setReportId(null);
    setSelectedElement(null);
    setPreviewMode(false);
    setPreviewData({});
    setLocalQueries([]);
    setSelectedQuery(null);
    toast.info('Nuovo report creato');
  };

  // Confirm new report with toast
  const confirmNewReport = () => {
    toast.warn(
      <div>
        <p style={{ margin: '0 0 10px 0' }}>Creare un nuovo report? Le modifiche non salvate verranno perse.</p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => toast.dismiss()}
            style={{ padding: '4px 12px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
          >
            Annulla
          </button>
          <button
            onClick={() => { toast.dismiss(); handleNewReport(); }}
            style={{ padding: '4px 12px', border: 'none', borderRadius: '4px', background: '#007bff', color: '#fff', cursor: 'pointer' }}
          >
            Nuovo Report
          </button>
        </div>
      </div>,
      { autoClose: false, closeButton: false }
    );
  };


  // Export to PDF
  const handleExportPDF = async () => {
    const { generatePDF } = await import('./utils/pdfGenerator');

    try {
      setLoading(true);

      // Use preview data if already loaded
      let data = previewData;

      // Check if we need to fetch data
      const needsFetch = !data || Object.keys(data).length === 0;

      if (needsFetch) {
        const usedQueryIds = getUsedQueryIds();

        if (usedQueryIds.length === 0) {
          // Allow PDF generation without data (static report)
          data = {};
        } else {
          data = {};
          for (const queryId of usedQueryIds) {
            // Find query in local queries
            const localQuery = localQueries.find(q => q.id === queryId);
            if (!localQuery) continue;

            const sql = localQuery.sql_query || localQuery.sql;
            if (!sql) continue;

            const execResponse = await ApiManager.sendAuthenticatedRequest('/utils/crystal_execute_query', {
              sql: sql,
              preview: false,
              limit: 10000
            });

            if (execResponse.success === 1 && execResponse.body?.data) {
              data[queryId] = {
                name: localQuery.name,
                columns: execResponse.body.columns,
                data: execResponse.body.data
              };
            }
          }
        }
      }

      const result = await generatePDF(report, data, entities);
      if (result.success) {
        toast.success('PDF generato');
      } else {
        toast.error(result.message || 'Errore generazione PDF');
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Errore generazione PDF');
    } finally {
      setLoading(false);
    }
  };

  // Toggle preview mode
  const handleTogglePreview = () => {
    if (!previewMode) {
      handleExecuteQuery();
    } else {
      setPreviewMode(false);
      toast.info('Modalità anteprima disattivata');
    }
  };

  // Save report to local file (JSON)
  const handleSaveToFile = () => {
    if (!report.name.trim()) {
      toast.warning('Inserisci un nome per il report');
      return;
    }

    const fileData = {
      version: '1.0',
      name: report.name,
      exportedAt: new Date().toISOString(),
      queries: localQueries.map(q => ({
        id: q.id,
        name: q.name,
        sql: q.sql_query || q.sql,
        author: q.author,
        createdAt: q.createdAt,
        updatedAt: q.updatedAt
      })),
      report: report
    };

    const blob = new Blob([JSON.stringify(fileData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.name.replace(/[^a-z0-9]/gi, '_')}.crystal.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Report salvato su file');
  };

  // Open report from local file
  const handleOpenFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const fileData = JSON.parse(e.target.result);

        // Validate file structure
        if (!fileData.report || !fileData.queries) {
          toast.error('File non valido: struttura mancante');
          return;
        }

        // Load report state
        dispatch({ type: 'SET_REPORT', payload: fileData.report });

        // Load queries and execute them
        const loadedQueries = fileData.queries.map(q => ({
          id: q.id,
          name: q.name,
          sql_query: q.sql,
          author: q.author,
          createdAt: q.createdAt,
          updatedAt: q.updatedAt
        }));
        setLocalQueries(loadedQueries);

        // Reset UI state
        setSelectedElement(null);
        setPreviewMode(false);
        setPreviewData({});
        setReportId(null);

        // Execute all queries to get fresh data
        if (loadedQueries.length > 0) {
          const queryResults = {};
          for (const query of loadedQueries) {
            try {
              const execResponse = await ApiManager.sendAuthenticatedRequest('/utils/crystal_execute_query', {
                sql: query.sql_query,
                preview: false,
                limit: 10000
              });

              if (execResponse.success === 1 && execResponse.body) {
                queryResults[query.id] = {
                  name: query.name,
                  columns: execResponse.body.columns,
                  data: execResponse.body.data
                };
              }
            } catch (err) {
              console.error(`Error executing query ${query.name}:`, err);
            }
          }
          setPreviewData(queryResults);
        }

        toast.success(`Report "${fileData.name}" caricato`);
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error('Errore nel caricamento del file');
      }
    };
    reader.readAsText(file);

    // Reset input so same file can be selected again
    event.target.value = '';
  };

  // Trigger file input click
  const handleOpenFileClick = () => {
    fileInputRef.current?.click();
  };

  if (loading && Object.keys(entities).length === 0) {
    return (
      <div className="crystalckd">
        <div className="loading-spinner">
          <FontAwesomeIcon icon="spinner" spin />
        </div>
      </div>
    );
  }

  return (
    <div className={`crystalckd ${previewMode ? 'preview-mode' : ''}`}>
      {/* Hidden file input for opening reports */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".json,.crystal.json"
        onChange={handleOpenFile}
      />

      {/* Toolbar */}
      <Toolbar
        report={report}
        onNew={confirmNewReport}
        onSave={handleSaveToFile}
        onLoad={handleOpenFileClick}
        onExport={handleExportPDF}
        onPreview={handleTogglePreview}
        previewMode={previewMode}
        onNameChange={(name) => dispatch({ type: 'SET_NAME', payload: name })}
      />

      {/* Main Content */}
      <div className="main-content">
        {/* Left Panel - Query Builder */}
        <div className="left-panel">
          <div className="tabs">
            <button
              className={`tab ${leftPanelTab === 'query' ? 'active' : ''}`}
              onClick={() => setLeftPanelTab('query')}
            >
              Query
            </button>
            <button
              className={`tab ${leftPanelTab === 'page' ? 'active' : ''}`}
              onClick={() => setLeftPanelTab('page')}
            >
              Pagina
            </button>
          </div>

          {leftPanelTab === 'query' && (
            <QueryPanel
              queries={localQueries}
              onQueriesChange={setLocalQueries}
              selectedQuery={selectedQuery}
              onSelectQuery={setSelectedQuery}
              onQueryDataLoaded={setQueryData}
              previewData={previewData}
              onPreviewDataChange={setPreviewData}
            />
          )}

          {leftPanelTab === 'page' && (
            <PageSettings
              settings={report.pageSettings}
              onSettingsChange={handlePageSettingsChange}
              onMarginsChange={handleMarginsChange}
            />
          )}
        </div>

        {/* Center Panel - Layout Editor */}
        <div className="center-panel">
          <LayoutEditor
            report={report}
            entities={entities}
            selectedElement={selectedElement}
            previewMode={previewMode}
            previewData={previewData}
            gridSettings={gridSettings}
            onGridSettingsChange={setGridSettings}
            onAddElement={handleAddElement}
            onUpdateElement={handleUpdateElement}
            onRemoveElement={handleRemoveElement}
            onSelectElement={handleSelectElement}
          />
        </div>

        {/* Right Panel - Entity Browser & Style Panel */}
        <div className={`right-panel ${rightPanelCollapsed ? 'collapsed' : ''}`}>
          {/* Collapse Toggle */}
          <button
            className="panel-collapse-btn"
            onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
            title={rightPanelCollapsed ? 'Espandi pannello' : 'Comprimi pannello'}
          >
            <FontAwesomeIcon icon={rightPanelCollapsed ? 'chevron-left' : 'chevron-right'} />
            <span>{rightPanelCollapsed ? '' : 'Comprimi'}</span>
          </button>

          <div className="tabs">
            <button
              className={`tab ${rightPanelTab === 'fields' ? 'active' : ''}`}
              onClick={() => setRightPanelTab('fields')}
            >
              Campi
            </button>
            <button
              className={`tab ${rightPanelTab === 'elements' ? 'active' : ''}`}
              onClick={() => setRightPanelTab('elements')}
            >
              Elementi
            </button>
            <button
              className={`tab ${rightPanelTab === 'style' ? 'active' : ''}`}
              onClick={() => setRightPanelTab('style')}
            >
              Stile
            </button>
          </div>

          {rightPanelTab === 'fields' && (
            <EntityBrowser
              entities={entities}
              selectedEntity={report.query.entity}
              onEntitySelect={handleEntitySelect}
            />
          )}

          {rightPanelTab === 'elements' && (
            <div className="elements-panel">
              {/* Copy/Paste toolbar */}
              <div className="elements-toolbar">
                <button
                  className={`elements-toolbar-btn ${!selectedElement ? 'disabled' : ''}`}
                  onClick={handleCopyElement}
                  disabled={!selectedElement}
                  title="Copia elemento (Ctrl+C)"
                >
                  <FontAwesomeIcon icon="copy" />
                  <span>Copia</span>
                </button>
                <button
                  className={`elements-toolbar-btn ${!clipboard ? 'disabled' : ''}`}
                  onClick={handlePasteElement}
                  disabled={!clipboard}
                  title="Incolla elemento (Ctrl+V)"
                >
                  <FontAwesomeIcon icon="paste" />
                  <span>Incolla</span>
                </button>
              </div>
              <div className="panel-content">
                {(report.layout.body?.elements || []).length === 0 ? (
                  <div className="empty-state">
                    <FontAwesomeIcon icon="layer-group" />
                    <p>Nessun elemento</p>
                    <p style={{ fontSize: '11px', marginTop: '8px' }}>Aggiungi elementi dal canvas</p>
                  </div>
                ) : (
                  <div className="elements-list">
                    {(report.layout.body?.elements || []).map((element, index) => {
                      const isSelected = selectedElement?.id === element.id;
                      const getElementIcon = (type) => {
                        switch (type) {
                          case 'text': return 'font';
                          case 'dataField': return 'database';
                          case 'table': return 'table';
                          case 'image': return 'image';
                          case 'pageNumber': return 'hashtag';
                          case 'rectangle': return 'square';
                          default: return 'cube';
                        }
                      };
                      const getElementLabel = (el) => {
                        switch (el.type) {
                          case 'text': return el.content || 'Testo';
                          case 'dataField': return el.field?.label || el.field?.name || 'Campo';
                          case 'table': return `Tabella (${el.columns?.length || 0} col.)`;
                          case 'image': return 'Immagine';
                          case 'pageNumber': return 'N° Pagina';
                          case 'rectangle': return 'Rettangolo';
                          default: return el.type;
                        }
                      };
                      return (
                        <div
                          key={element.id}
                          className={`element-list-item ${isSelected ? 'selected' : ''} ${draggedElementIndex === index ? 'dragging' : ''}`}
                          onClick={() => handleSelectElement('body', element.id)}
                          draggable
                          onDragStart={(e) => handleElementDragStart(e, index)}
                          onDragOver={(e) => handleElementDragOver(e, index)}
                          onDrop={(e) => handleElementDrop(e, index)}
                          onDragEnd={handleElementDragEnd}
                        >
                          <span className="element-list-drag-handle">
                            <FontAwesomeIcon icon="grip-vertical" />
                          </span>
                          <span className="element-list-icon">
                            <FontAwesomeIcon icon={getElementIcon(element.type)} />
                          </span>
                          <span className="element-list-label">{getElementLabel(element)}</span>
                          <span className="element-list-type">{element.type}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {rightPanelTab === 'style' && (
            <StylePanel
              element={getSelectedElementData()}
              availableFields={report.query.selectedFields}
              onStyleChange={handleStyleChange}
            />
          )}
        </div>
      </div>

    </div>
  );
}
